import { Add as AddIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "../components/page-header";
import { SearchBar } from "../components/search-bar";
import { TestChatStateChip } from "../components/test-chat-state-chip";
import { useDebounce } from "../hooks";
import { usePaginatedApiQuery } from "../hooks/use-paginated-api-query";
import type { TestChat } from "../types/test-chat";
import { clickableProps } from "../utils/keyboard";

export default function TestChatsPage() {
  const nav = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState<string>("");

  const {
    data: chats,
    isLoading,
    refetch: refetchChats,
    setFilters,
  } = usePaginatedApiQuery<TestChat>("/test-chats", {
    page: 0,
    pageSize: 100,
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      setFilters((prev) => ({
        ...prev,
        q: debouncedSearchQuery,
      }));
    }
  }, [debouncedSearchQuery, setFilters]);

  // Remove the mutation since we're navigating to home instead

  return (
    <Box data-testid="test-chats-page">
      <PageHeader
        title="Chats"
        subtitle="See all the chats you have created."
        actions={
          <>
            <Tooltip title="New Chat" placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="New Chat"
                  onClick={() => nav("/")}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    border: "0px solid",
                    borderColor: "divider",
                    color: "grey.800",
                    backgroundColor: "common.white",
                    "&:hover": {
                      borderColor: "info.dark",
                      backgroundColor: "background.default",
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh" placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Refresh"
                  onClick={() => refetchChats()}
                  disabled={isLoading}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    border: "0px solid",
                    borderColor: "divider",
                    color: "grey.800",
                    backgroundColor: "common.white",
                    "&:hover": {
                      borderColor: "info.dark",
                      backgroundColor: "background.default",
                    },
                    "&:disabled": {
                      borderColor: "divider",
                      backgroundColor: "grey.100",
                      color: "grey.500",
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SearchBar
              size="small"
              value={searchQuery}
              onChange={setSearchQuery}
              loading={isLoading}
              label="Search chats..."
              sx={{
                width: "100%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "text.secondary",
                },
                "& .MuiOutlinedInput-input": { fontSize: "0.8rem" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "info.dark",
                },
              }}
            />
          </Box>
        </Stack>
      </PageHeader>
      {isLoading && (
        <Typography variant="body1" color="text.secondary">
          Loading chats...
        </Typography>
      )}
      {chats?.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          No chats found.
        </Typography>
      )}
      {chats?.map((chat) => (
        <Stack
          key={chat.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
            padding: "16px",
            marginBottom: "16px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "grey.100",
            },
          }}
          {...clickableProps(() => {
            nav(`/test-chats/${chat.id}`);
          })}
          onClick={() => {
            nav(`/test-chats/${chat.id}`);
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TestChatStateChip state={chat.state} size="small" />
            <Typography variant="h6">{chat.title}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(chat.created_at), "MMM dd, yyyy HH:mm")}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}
